/**
 * Platform Detection Utility
 * Detects whether the app is running in native mobile app or web browser
 */

/**
 * Check if running as a native mobile app (iOS/Android via Apilix or other wrapper)
 * @returns {boolean} True if running in native mobile app
 */
export const isMobileApp = () => {
  if (typeof window === 'undefined') return false;
  
  try {
    // Check for Apilix or other native app wrappers
    const isApilix = window.Apilix !== undefined || 
                     window.webkit?.messageHandlers?.Apilix !== undefined ||
                     window.Android !== undefined; // Android bridge
    
    // Check if running on mobile device
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Check for standalone mode (PWA or native app wrapper)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true || // iOS Safari standalone
                         document.referrer.includes('android-app://'); // Android TWA
    
    // Check if loaded from file:// protocol (common in native apps)
    const isFileProtocol = window.location.protocol === 'file:';
    
    // Check for native app user agent markers (WebView)
    const hasNativeMarker = /wv|WebView|Apilix|NativeApp/i.test(navigator.userAgent);
    
    // Android-specific checks
    const isAndroidWebView = /Android/i.test(navigator.userAgent) && /wv|WebView/i.test(navigator.userAgent);
    const isAndroidStandalone = /Android/i.test(navigator.userAgent) && isStandalone;
    
    // Check if running in an app wrapper (not regular mobile browser)
    const isWrappedApp = isMobileDevice && !window.opener && window.self === window.top;
    
    // Return true if any native app indicator is found
    const result = isApilix || 
                   isFileProtocol || 
                   hasNativeMarker || 
                   isAndroidWebView ||
                   isAndroidStandalone ||
                   (isMobileDevice && isStandalone) ||
                   (isWrappedApp && (window.Android || window.webkit));
    
    return result;
  } catch (error) {
    console.error('[PlatformDetection] Error detecting mobile app:', error);
    return false; // Fail safe to web mode
  }
};

/**
 * Check if running on iOS specifically
 * @returns {boolean} True if running on iOS
 */
export const isIOS = () => {
  if (typeof window === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/**
 * Check if running on Android specifically
 * @returns {boolean} True if running on Android
 */
export const isAndroid = () => {
  if (typeof window === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
};

/**
 * Open external URL in system browser (for mobile apps)
 * For web browsers, opens in new tab
 * @param {string} url - The URL to open
 */
export const openExternalURL = (url) => {
  if (typeof window === 'undefined') return;
  
  // Try Apilix native bridge
  if (window.Apilix && window.Apilix.openExternalBrowser) {
    window.Apilix.openExternalBrowser(url);
    return;
  }
  
  // Try iOS WebKit message handler
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openExternal) {
    window.webkit.messageHandlers.openExternal.postMessage(url);
    return;
  }
  
  // Try Android interface
  if (window.Android && window.Android.openExternalBrowser) {
    window.Android.openExternalBrowser(url);
    return;
  }
  
  // Fallback: open in new tab for web browsers
  if (isMobileApp()) {
    window.location.href = url; // Last resort for mobile apps
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Get the base web URL for the app
 * @returns {string} The base URL
 */
export const getWebBaseURL = () => {
  return 'https://www.dejavunomad.com';
};

/**
 * Open AI Concierge in external browser (for mobile apps)
 * This allows users to access premium features via web without IAP
 * @param {string} additionalPath - Optional additional path after /ai-concierge
 */
export const openAIConciergeinBrowser = (additionalPath = '') => {
  const baseURL = getWebBaseURL();
  const fullURL = `${baseURL}/ai-concierge${additionalPath}`;
  openExternalURL(fullURL);
};
